import React, { useEffect, useRef, useState } from "react";
import { Mic, StopCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  MAX_VOICE_SECONDS,
  MIN_VOICE_SECONDS,
  isRecordingSupported,
  pickAudioMimeType,
  formatDuration,
} from "@/lib/voice";
import { useLang } from "@/lib/i18n";

/**
 * Compact mic button that expands into an inline recording controller.
 *
 * Contract:
 * - `onSend(blob, durationSeconds, mimeType)` — async; called only after the
 *   user taps stop with ≥ 1 s recorded. Parent handles the HTTP upload.
 * - `disabled` — hides interaction while chat is not sendable.
 * - Automatically stops + submits at 60 s.
 * - Does NOT save anything locally beyond the in-memory blob it hands to
 *   `onSend`. No caching, no offline queue.
 */
export function VoiceRecorder({ disabled, onSend, size = 40 }) {
  const { t } = useLang();
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickerRef = useRef(null);
  const stopArmedRef = useRef(false);
  const cancelledRef = useRef(false);
  const supported = isRecordingSupported();

  const cleanupStream = () => {
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
  };

  useEffect(() => () => {
    cleanupStream();
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
  }, []);

  const start = async () => {
    if (state !== "idle") return;
    if (!supported) {
      toast.error(t("voice_not_supported_toast"));
      return;
    }
    const mimeType = pickAudioMimeType();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      const options = mimeType ? { mimeType } : {};
      let rec;
      try {
        rec = new window.MediaRecorder(stream, options);
      } catch {
        // Some browsers reject the options object outright; retry without.
        rec = new window.MediaRecorder(stream);
      }
      recorderRef.current = rec;
      chunksRef.current = [];
      cancelledRef.current = false;
      stopArmedRef.current = false;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const durationSec = Math.max(0, (Date.now() - startedAtRef.current) / 1000);
        cleanupStream();
        const effectiveType = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: effectiveType });
        chunksRef.current = [];
        setElapsed(0);

        if (cancelledRef.current) {
          setState("idle");
          return;
        }
        if (durationSec < MIN_VOICE_SECONDS) {
          toast.message(t("voice_hold_title"), {
            description: t("voice_hold_desc"),
          });
          setState("idle");
          return;
        }
        if (!blob || blob.size === 0) {
          toast.error(t("voice_failed"));
          setState("idle");
          return;
        }
        setState("uploading");
        try {
          await onSend(blob, Math.min(durationSec, MAX_VOICE_SECONDS), effectiveType);
        } catch (err) {
          toast.error(err?.message || "Failed to send voice message.");
        } finally {
          setState("idle");
        }
      };
      rec.onerror = () => {
        cleanupStream();
        setState("idle");
        toast.error(t("voice_error"));
      };

      startedAtRef.current = Date.now();
      setElapsed(0);
      setState("recording");
      rec.start(250); // small timeslice so Safari flushes data even on short clips
      tickerRef.current = setInterval(() => {
        const s = (Date.now() - startedAtRef.current) / 1000;
        setElapsed(s);
        if (s >= MAX_VOICE_SECONDS && !stopArmedRef.current) {
          stopArmedRef.current = true;
          try { rec.state === "recording" && rec.stop(); } catch {}
        }
      }, 200);
    } catch (err) {
      cleanupStream();
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error(t("voice_perm_denied"));
      } else if (name === "NotFoundError") {
        toast.error(t("voice_no_mic"));
      } else {
        toast.error(t("voice_no_access"));
      }
      setState("idle");
    }
  };

  const stop = () => {
    if (state !== "recording" || !recorderRef.current) return;
    cancelledRef.current = false;
    stopArmedRef.current = true;
    try { recorderRef.current.stop(); } catch {}
  };

  const cancel = () => {
    if (state !== "recording" || !recorderRef.current) return;
    cancelledRef.current = true;
    stopArmedRef.current = true;
    try { recorderRef.current.stop(); } catch {}
  };

  // ---------- rendering ----------
  if (state === "idle" || state === "uploading") {
    const busy = state === "uploading";
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled || busy || !supported}
        title={supported ? t("voice_record_tt") : t("voice_not_supported_tt")}
        aria-label={t("voice_record_tt")}
        data-testid="voice-record-button"
        className="inline-flex items-center justify-center rounded-full transition-transform hover:-translate-y-[1px] disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          width: size, height: size,
          border: "1px solid rgba(0,229,255,0.35)",
          background: busy ? "rgba(0,229,255,0.15)" : "rgba(0,229,255,0.06)",
          color: "var(--accent)",
        }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
      </button>
    );
  }

  // recording — replaces the input row with a live meter
  const capped = elapsed >= MAX_VOICE_SECONDS - 0.05;
  return (
    <div
      className="flex items-center gap-2 rounded-full px-2 py-1"
      style={{
        border: "1px solid rgba(255,51,51,0.55)",
        background: "rgba(255,51,51,0.08)",
        minHeight: size,
      }}
      data-testid="voice-recording-bar"
    >
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={{ background: "#ff3333", boxShadow: "0 0 0 4px rgba(255,51,51,0.18)", animation: "voicePulse 1.1s ease-in-out infinite" }}
      />
      <span className="text-[12px] font-semibold tracking-wide text-white tabular-nums" data-testid="voice-recording-elapsed">
        {formatDuration(elapsed)} / 1:00
      </span>
      <button
        type="button"
        onClick={cancel}
        title={t("voice_cancel_tt")}
        aria-label={t("voice_cancel_tt")}
        data-testid="voice-cancel"
        className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full text-white/70 hover:text-white"
        style={{ background: "rgba(255,255,255,0.06)" }}
      >
        <X size={14} />
      </button>
      <button
        type="button"
        onClick={stop}
        title={capped ? t("voice_send_tt") : t("voice_stop_send_tt")}
        aria-label={t("voice_stop_send_tt")}
        data-testid="voice-stop-send"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full"
        style={{ background: "#ff3333", color: "#fff" }}
      >
        <StopCircle size={16} />
      </button>
      <style>{`
        @keyframes voicePulse {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
